import {
    Account,
    AccountAddress,
} from '../../../../shared/types/modules/account';
import { ISelect } from '../../../../shared/types/shared';
import { OrbitCitizenInfoValues } from '../citizenInfo/formData';
import { OrbitSpecialConditionValues } from '../specialCondition/formData';

const asSelect = (value?: string | ISelect): ISelect | undefined =>
    value && typeof value === 'object' ? value : undefined;

const paramCodeToOrbitValue = (code?: string, prefix?: string): string => {
    if (!code) return '';
    if (prefix && code.startsWith(prefix)) {
        return code.slice(prefix.length);
    }
    return code;
};

const PERSON_TYPE_MAP: Record<string, string> = {
    PERSON_TYPE_NAT: 'NATURAL',
    PERSON_TYPE_JUR: 'JURIDICA',
};

const mapPersonType = (personType?: string | ISelect): string => {
    const select = asSelect(personType);
    const code = select?.code ?? '';
    return PERSON_TYPE_MAP[code] ?? paramCodeToOrbitValue(code, 'PERSON_TYPE_');
};

const mapDocumentType = (typeDocument?: string | ISelect): string => {
    const select = asSelect(typeDocument);
    return paramCodeToOrbitValue(select?.code, 'DOCUMENT_TYPE_');
};

const resolveOrbitCatalogValue = (
    param?: string | ISelect,
    catalogItems?: ISelect[]
): string => {
    const select = asSelect(param);
    if (!select) return typeof param === 'string' ? param : '';

    const byCode = catalogItems?.find(
        (item) =>
            item.code === select.code ||
            item.id === select.code ||
            item.id === paramCodeToOrbitValue(select.code, 'GENDER_')
    );
    if (byCode) return String(byCode.id);

    const byName = catalogItems?.find(
        (item) =>
            item.name.localeCompare(select.name ?? '', 'es', {
                sensitivity: 'base',
            }) === 0
    );
    if (byName) return String(byName.id);

    return paramCodeToOrbitValue(select.code, 'GENDER_');
};

const resolveOrbitCityId = (
    city?: string | ISelect,
    orbitCities?: ISelect[]
): string => {
    if (!city) return '';
    if (typeof city === 'string') return city;

    const match = orbitCities?.find(
        (orbitCity) =>
            orbitCity.name.localeCompare(city.name ?? '', 'es', {
                sensitivity: 'base',
            }) === 0 ||
            orbitCity.code === city.code ||
            String(orbitCity.id) === String(city.id)
    );

    return match ? String(match.id) : '';
};

const resolveAddressType1 = (
    type1?: string | ISelect,
    orbitAddressTypes?: ISelect[]
): string => {
    const select = asSelect(type1);
    const rawValue =
        select?.name ||
        select?.code ||
        (typeof type1 === 'string' ? type1 : '');

    if (!rawValue) return '';

    const match = orbitAddressTypes?.find(
        (item) =>
            item.name.localeCompare(rawValue, 'es', {
                sensitivity: 'base',
            }) === 0 ||
            String(item.id) === String(select?.id) ||
            String(item.id) === rawValue ||
            item.code === select?.code
    );

    return match ? String(match.id) : '';
};

export const mapAccountToCitizenInfo = (
    account: Account,
    orbitGenders?: ISelect[]
): Partial<OrbitCitizenInfoValues> => ({
    personType: mapPersonType(account.personType),
    typeDocument: mapDocumentType(account.typeDocument),
    document: account.document || '',
    name: account.name || '',
    lastName: account.lastName || '',
    email: account.email || '',
    cellPhone: account.cellPhone || '',
    phone: account.phone || '',
    gender: resolveOrbitCatalogValue(account.gender, orbitGenders),
    isLGBTIQ: account.isLGBTIQ ?? false,
});

export const mapAccountToAddress = (
    account: Account,
    orbitCities?: ISelect[],
    orbitAddressTypes?: ISelect[]
): Partial<AccountAddress> => {
    const address = account.address;
    if (!address) return {};

    return {
        city: resolveOrbitCityId(address.city, orbitCities),
        type1: resolveAddressType1(address.type1, orbitAddressTypes),
        type2: address.type2 || '',
        number1: address.number1 || '',
        letter1: address.letter1 || '',
        letter2: address.letter2 || '',
        quadrant1: address.quadrant1 || '',
        number2: address.number2 || '',
        letter3: address.letter3 || '',
        letter4: address.letter4 || '',
        quadrant2: address.quadrant2 || '',
        number3: address.number3 || '',
        quadrant3: address.quadrant3 || '',
        complement: address.complement || '',
        address: address.address || '',
    };
};

export const mapAccountToSpecialCondition = (
    account: Account,
    orbitSpecialConditions?: ISelect[]
): Partial<OrbitSpecialConditionValues> => {
    const specialCondition = account.specialCondition;
    if (!specialCondition) return {};

    return {
        hasSpecialCondition: Boolean(specialCondition.hasSpecialCondition),
        specialCondition: resolveOrbitCatalogValue(
            specialCondition.specialCondition,
            orbitSpecialConditions
        ),
        observations: specialCondition.observations || '',
    };
};
